/**
 * <ta-upload> — drag-drop ONE OR MANY CSVs, route by filename to the
 * right parser (Rapsodo or Pocket Radar), parse, write to Supabase.
 *
 * Multi-file flow (issue #4):
 *   idle  → processing (live per-file status)  → summary (final report)
 * From summary, "Create →" on any no-athlete row opens:
 *   creating (the create-athlete form for that pitcher)  → back to summary
 *
 * Routing (filename-based):
 *   PracticeSession_<24hex>_Data_*.csv  →  Rapsodo (rapsodoSource)
 *   PR_*.csv                            →  Pocket Radar (pocketRadarSource)
 *   anything else                       →  status: 'invalid-filename'
 * Both sources flow through ingestParsedSource (src/db/ingest.js).
 *
 * Each file is processed sequentially (no parallelism — at TAP scale,
 * sequential keeps Supabase load tame and the UI status easy to follow).
 *
 * Emits 'upload-complete' (bubbling) when the batch finishes AND when a
 * no-athlete retry completes — the parent re-fetches its view either way.
 *
 * NOTE for MVP-1.5: Pocket Radar's effort_target_pct is currently passed
 * as null (no UI prompt yet). Coach can edit per-upload later. The PR
 * chart still works without it — just no band coloring.
 */

import { ingestParsedSource } from '../../db/ingest.js';
import { rapsodoSource } from '../../features/rapsodo/source.js';
import { pocketRadarSource } from '../../features/pocket-radar/source.js';
import { extractSessionId, isRapsodoCsv } from '../../features/rapsodo/column-map.js';
import { extractFilenameKey, isPocketRadarCsv } from '../../features/pocket-radar/column-map.js';
import { createAthlete } from '../../db/queries.js';

// Filename-first detection. Coaches commonly rename Rapsodo exports for
// organization, which used to break routing — now the content sniffers
// below act as a fallback when the filename pattern doesn't match.
function detectUploaderTypeFromFilename(filename) {
  if (extractSessionId(filename)) return 'rapsodo';
  if (extractFilenameKey(filename)) return 'pocket-radar';
  return 'unknown';
}

// Content sniffer fallback — runs only when filename detection didn't
// resolve. Reads the CSV header row and matches Rapsodo / PR signatures.
function detectUploaderTypeFromContent(csvText) {
  if (isRapsodoCsv(csvText)) return 'rapsodo';
  if (isPocketRadarCsv(csvText)) return 'pocket-radar';
  return 'unknown';
}

async function dispatchUpload({ uploaderType, csvText, csvFile, filename }) {
  if (uploaderType === 'rapsodo') {
    return ingestParsedSource({ csvText, csvFile, filename, descriptor: rapsodoSource });
  }
  if (uploaderType === 'pocket-radar') {
    return ingestParsedSource({
      csvText, csvFile, filename,
      descriptor: pocketRadarSource,
      sourceParams: { effortTargetPct: null },
    });
  }
  return { status: 'invalid-filename', filename };
}

class TaUpload extends HTMLElement {
  constructor() {
    super();
    this.state = 'idle';
    this._files = [];
    this._currentIndex = -1;
  }

  connectedCallback() {
    this.render();

    this._onDragOver = (e) => {
      e.preventDefault();
      this.querySelector('.ta-upload__zone')?.classList.add('is-hover');
    };
    this._onDragLeave = () => {
      this.querySelector('.ta-upload__zone')?.classList.remove('is-hover');
    };
    this._onDrop = (e) => {
      e.preventDefault();
      this.querySelector('.ta-upload__zone')?.classList.remove('is-hover');
      const files = e.dataTransfer?.files;
      console.log('[ta-upload] drop event', { fileCount: files?.length });
      if (files && files.length > 0) this.handleFiles(files);
      else console.warn('[ta-upload] drop fired but dataTransfer.files was empty');
    };
    this._onClick = (e) => {
      // Browse-button click is now handled natively by <label>+<input> —
      // no programmatic .click() needed.
      if (e.target.matches('.ta-upload__reset')) {
        this.reset();
      } else if (e.target.matches('[data-action="create"]')) {
        const idx = parseInt(e.target.dataset.index, 10);
        if (Number.isInteger(idx)) this.startCreateAthlete(idx);
      } else if (e.target.matches('[data-action="cancel-create"]')) {
        this.state = 'summary';
        this._currentIndex = -1;
        this.render();
      }
    };
    this._onChange = (e) => {
      if (e.target.matches('input[type=file]')) {
        const files = e.target.files;
        console.log('[ta-upload] change event', { fileCount: files?.length });
        if (files && files.length > 0) this.handleFiles(files);
        else console.warn('[ta-upload] change fired but input.files was empty');
      }
    };
    this._onSubmit = (e) => {
      if (e.target.matches('.create-athlete-form')) {
        e.preventDefault();
        this.handleCreateAthlete(e.target);
      }
    };

    this.addEventListener('dragover', this._onDragOver);
    this.addEventListener('dragleave', this._onDragLeave);
    this.addEventListener('drop', this._onDrop);
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    this.addEventListener('submit', this._onSubmit);
  }

  reset() {
    this.state = 'idle';
    this._files = [];
    this._currentIndex = -1;
    this.render();
  }

  async handleFiles(fileList) {
    const all = Array.from(fileList);
    const csvs = all.filter((f) => f.name.toLowerCase().endsWith('.csv'));
    const skipped = all.length - csvs.length;

    if (csvs.length === 0) {
      this._files = [];
      this.state = 'summary';
      this._summaryNote = `Dropped ${all.length} file${all.length === 1 ? '' : 's'}, none were .csv. Only Rapsodo CSV exports are accepted.`;
      this.render();
      return;
    }

    this._files = csvs.map((file) => ({
      file,
      // Optimistic filename-based guess; processFile() finalizes via
      // content sniffer when the filename didn't match a known pattern
      // (most often: coach-renamed Rapsodo exports).
      uploaderType: detectUploaderTypeFromFilename(file.name),
      status: 'pending',
      result: null,
      error: null,
    }));
    this._summaryNote = skipped > 0
      ? `${skipped} non-CSV file${skipped === 1 ? '' : 's'} skipped.`
      : '';
    this.state = 'processing';
    this.render();

    for (let i = 0; i < this._files.length; i++) {
      await this.processFile(i);
    }

    this.state = 'summary';
    this.render();
    this.dispatchEvent(
      new CustomEvent('upload-complete', { bubbles: true, detail: { files: this._files } })
    );
  }

  async processFile(index) {
    const item = this._files[index];
    item.status = 'parsing';
    this.render();
    try {
      const csvText = await item.file.text();
      // If filename detection failed, try content sniffer — this is the
      // path coach-renamed Rapsodo exports take. Persist the resolved
      // type on the item so create-athlete retries use it later.
      if (item.uploaderType === 'unknown') {
        item.uploaderType = detectUploaderTypeFromContent(csvText);
      }
      item.result = await dispatchUpload({
        uploaderType: item.uploaderType,
        csvText,
        csvFile: item.file,
        filename: item.file.name,
      });
    } catch (err) {
      item.error = err;
      item.result = { status: 'error' };
    }
    item.status = 'done';
    this.render();
  }

  startCreateAthlete(index) {
    if (!this._files[index] || this._files[index].result?.status !== 'no-athlete') return;
    this._currentIndex = index;
    this.state = 'creating';
    this.render();
  }

  async handleCreateAthlete(form) {
    const fd = new FormData(form);
    const data = {
      first_name: fd.get('first_name'),
      last_name: fd.get('last_name'),
      athlete_age: fd.get('athlete_age') || null,
      playing_age: fd.get('playing_age') || null,
      throws_hand: fd.get('throws_hand') || 'R',
      nickname: fd.get('nickname') || null,
    };

    const item = this._files[this._currentIndex];
    if (!item) {
      this.state = 'summary';
      this.render();
      return;
    }

    item.status = 'parsing';
    this.state = 'processing';
    this.render();

    try {
      await createAthlete(data);
      const csvText = await item.file.text();
      item.result = await dispatchUpload({
        uploaderType: item.uploaderType,
        csvText,
        csvFile: item.file,
        filename: item.file.name,
      });
      item.error = null;
    } catch (err) {
      item.error = err;
      item.result = { status: 'error' };
    }
    item.status = 'done';
    this._currentIndex = -1;
    this.state = 'summary';
    this.render();
    this.dispatchEvent(
      new CustomEvent('upload-complete', { bubbles: true, detail: { files: this._files } })
    );
  }

  render() {
    this.innerHTML = TEMPLATES[this.state]?.(this) ?? TEMPLATES.idle(this);
  }
}

// ============================================================
// Templates
// ============================================================

const TEMPLATES = {
  idle: () => /* html */ `
    <label class="ta-upload__zone">
      <p class="ta-upload__heading">Drop Rapsodo or Pocket Radar CSVs here</p>
      <p class="ta-upload__hint">
        Single file or many at once. We detect the type by filename.
        Or <span class="ta-upload__browse">click to browse</span>.
      </p>
      <input type="file" accept=".csv" multiple class="ta-upload__file-input">
    </label>
  `,

  processing: (host) => {
    const total = host._files.length;
    const done = host._files.filter((f) => f.status === 'done').length;
    return /* html */ `
      <div class="ta-upload__zone is-busy">
        <p class="ta-upload__heading">Processing ${done} of ${total}…</p>
        <ul class="ta-upload__file-list">
          ${host._files.map((item, idx) => renderFileRow(item, idx)).join('')}
        </ul>
      </div>
    `;
  },

  summary: (host) => {
    const counts = countResults(host._files);
    const totalPitches = host._files
      .filter((f) => f.result?.status === 'success')
      .reduce((sum, f) => sum + (f.result.pitchCount ?? 0), 0);
    const empty = host._files.length === 0;

    return /* html */ `
      <div class="ta-upload__zone is-summary">
        <p class="ta-upload__heading">${empty ? 'Nothing to upload' : 'Batch complete'}</p>
        ${host._summaryNote ? `<p class="ta-upload__hint">${escapeHtml(host._summaryNote)}</p>` : ''}
        ${empty ? '' : `
          <ul class="ta-upload__file-list">
            ${host._files.map((item, idx) => renderFileRow(item, idx)).join('')}
          </ul>
          <p class="ta-upload__stats">
            <strong>${counts.success}</strong> uploaded
            ${counts.duplicate > 0 ? ` · <strong>${counts.duplicate}</strong> duplicate${counts.duplicate === 1 ? '' : 's'}` : ''}
            ${counts.noAthlete > 0 ? ` · <strong>${counts.noAthlete}</strong> awaiting athlete` : ''}
            ${counts.warn > 0 ? ` · <strong>${counts.warn}</strong> warning${counts.warn === 1 ? '' : 's'}` : ''}
            ${counts.error > 0 ? ` · <strong>${counts.error}</strong> error${counts.error === 1 ? '' : 's'}` : ''}
            · <strong>${totalPitches}</strong> pitch${totalPitches === 1 ? '' : 'es'} inserted
          </p>
        `}
        <button type="button" class="ta-upload__reset">Upload another batch</button>
      </div>
    `;
  },

  creating: (host) => {
    const item = host._files[host._currentIndex];
    if (!item) return TEMPLATES.summary(host);
    const pitcher = item.result.pitcher;
    const first = escapeHtml(pitcher.first);
    const last = escapeHtml(pitcher.last);
    const filename = escapeHtml(item.file.name);
    const pitchCount = item.result.pitchCount;
    return /* html */ `
      <div class="ta-upload__zone is-warn">
        <p class="ta-upload__heading">New athlete: ${first} ${last}</p>
        <p class="ta-upload__hint">
          From <strong>${filename}</strong> (${pitchCount} pitch${pitchCount === 1 ? '' : 'es'}).
          Fill in DOB and throwing hand to create them and attach this upload.
        </p>

        <form class="create-athlete-form">
          <div class="form-row">
            <label class="form-field">
              <span class="form-field__label">First name</span>
              <input type="text" name="first_name" value="${first}" required>
            </label>
            <label class="form-field">
              <span class="form-field__label">Last name</span>
              <input type="text" name="last_name" value="${last}" required>
            </label>
          </div>

          <div class="form-row">
            <label class="form-field">
              <span class="form-field__label">Athlete age <span class="form-field__optional">(years, optional)</span></span>
              <input type="number" name="athlete_age" min="5" max="60" placeholder="e.g. 13">
            </label>
            <label class="form-field">
              <span class="form-field__label">Playing age <span class="form-field__optional">(competitive class, optional)</span></span>
              <input type="text" name="playing_age" placeholder="e.g. 13U, 14U, HS">
            </label>
          </div>

          <fieldset class="form-field throws-toggle">
            <legend class="form-field__label">Throws</legend>
            <label><input type="radio" name="throws_hand" value="R" checked> R</label>
            <label><input type="radio" name="throws_hand" value="L"> L</label>
            <label><input type="radio" name="throws_hand" value="S"> S</label>
          </fieldset>

          <label class="form-field">
            <span class="form-field__label">Nickname <span class="form-field__optional">(optional)</span></span>
            <input type="text" name="nickname" placeholder="e.g. TT">
          </label>

          <p class="form-hint">
            Future uploads with the same pitcher name will auto-link to this athlete.
            Editing the name above creates a duplicate if the CSV name doesn't match.
          </p>

          <div class="form-actions">
            <button type="button" class="ta-upload__reset" data-action="cancel-create">Back to summary</button>
            <button type="submit" class="ta-upload__submit">Create &amp; upload</button>
          </div>
        </form>
      </div>
    `;
  },
};

// ============================================================
// Helpers
// ============================================================

function renderFileRow(item, idx) {
  const filename = escapeHtml(item.file.name);

  if (item.status === 'pending') {
    return `<li class="file-row file-row--pending">
      <span class="file-row__icon">⊙</span>
      <span class="file-row__name">${filename}</span>
      <span class="file-row__status">queued</span>
    </li>`;
  }
  if (item.status === 'parsing') {
    return `<li class="file-row file-row--parsing">
      <span class="file-row__icon">⏳</span>
      <span class="file-row__name">${filename}</span>
      <span class="file-row__status">processing…</span>
    </li>`;
  }

  const r = item.result ?? {};
  switch (r.status) {
    case 'success':
      return `<li class="file-row file-row--ok">
        <span class="file-row__icon">✓</span>
        <span class="file-row__name">${filename}</span>
        <span class="file-row__status">${r.pitchCount} pitches → ${escapeHtml(r.athleteName)}</span>
      </li>`;
    case 'duplicate':
      return `<li class="file-row file-row--neutral">
        <span class="file-row__icon">⊝</span>
        <span class="file-row__name">${filename}</span>
        <span class="file-row__status">already uploaded</span>
      </li>`;
    case 'no-athlete':
      return `<li class="file-row file-row--warn">
        <span class="file-row__icon">⚠</span>
        <span class="file-row__name">${filename}</span>
        <span class="file-row__status">no athlete: ${escapeHtml(r.pitcher.first)} ${escapeHtml(r.pitcher.last)}</span>
        <button type="button" class="file-row__action" data-action="create" data-index="${idx}">Create →</button>
      </li>`;
    case 'invalid-filename':
      return `<li class="file-row file-row--warn">
        <span class="file-row__icon">⚠</span>
        <span class="file-row__name">${filename}</span>
        <span class="file-row__status">not a Rapsodo filename pattern</span>
      </li>`;
    case 'empty':
      return `<li class="file-row file-row--warn">
        <span class="file-row__icon">⚠</span>
        <span class="file-row__name">${filename}</span>
        <span class="file-row__status">no valid pitches (all rows filtered)</span>
      </li>`;
    case 'error':
    default:
      return `<li class="file-row file-row--error">
        <span class="file-row__icon">✗</span>
        <span class="file-row__name">${filename}</span>
        <span class="file-row__status">${escapeHtml(item.error?.message ?? 'error')}</span>
      </li>`;
  }
}

function countResults(files) {
  const counts = { success: 0, duplicate: 0, noAthlete: 0, warn: 0, error: 0 };
  for (const f of files) {
    const s = f.result?.status;
    if (s === 'success') counts.success++;
    else if (s === 'duplicate') counts.duplicate++;
    else if (s === 'no-athlete') counts.noAthlete++;
    else if (s === 'invalid-filename' || s === 'empty') counts.warn++;
    else counts.error++;
  }
  return counts;
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

customElements.define('ta-upload', TaUpload);
